<template>
  <FullScreen
    ref="screen"
    @pointermove="handleInteract"
    @pointerleave="handlePointerLeave"
  >
    <div :class="className">
      <div :class="ns.e('main')">
        <div
          :class="[ns.e('player'), flipped ? ns.em('player', 'flipped') : '']"
          @click="() => togglePlaying()"
        >
          <slot name="player">
            <video
              ref="video"
              v-bind="videoAttrs"
              :class="ns.e('video')"
              :src="currentSrc || videoAttrs?.src"
            >
              <slot />
            </video>
          </slot>
        </div>

        <div
          v-if="!hasPlayed && (poster || $slots.poster)"
          :class="ns.e('poster')"
        >
          <slot name="poster">
            <img :src="poster" />
          </slot>
        </div>

        <Transition
          :name="ns.b('state-effect')"
          @after-leave="() => (stateHidden = true)"
        >
          <div v-if="canPlay && stateShow" :class="ns.e('state')">
            <slot name="state" :active="stateShow">
              <lt-icon :size="56">
                <VideoPause v-if="playing" />
                <VideoPlay v-else />
              </lt-icon>
            </slot>
          </div>
        </Transition>

        <Transition :name="`${defaultNamespace}-fade`">
          <div
            v-if="(!canPlay && !stateShow) || (loading && stateHidden)"
            :class="ns.e('loading')"
          >
            <slot name="loading">
              <lt-icon :size="56"><Loading /></lt-icon>
            </slot>
          </div>
        </Transition>

        <Progress
          v-if="noControls"
          :class="ns.e('progress-line')"
          :percentage="percent"
          :stroke-width="3"
          :show-text="false"
        />

        <div
          v-else
          :class="{
            [ns.e('controls')]: true,
            [ns.em('controls', 'collapsed')]: hasPlayed && !interacting,
          }"
        >
          <section :class="ns.e('controls-top')">
            <VideoProgress
              :time="currentTime"
              :duration="duration"
              :segments="segments"
              @change="changeTime"
            />
          </section>

          <section :class="ns.e('controls-bottom')">
            <div
              v-for="(item, k) in controlBottomList"
              :key="k"
              :class="item.class"
            >
              <template v-for="(name, i) in item.list || []" :key="i">
                <slot :name="`control-${name}`" v-bind="slotParams">
                  <control
                    :key="i"
                    :name="name"
                    :full="full"
                    :current-time="currentTime"
                    :current-volume="currentVolume"
                    :can-play="canPlay"
                    :pip="pip"
                    :playing="playing"
                    :duration="duration"
                    :play-prev-disabled="!srcList || !srcIndex"
                    :play-next-disabled="
                      !srcList || srcIndex === srcFullList.length - 1
                    "
                    :shortcuts="shortcuts"
                    :playback-rates="playbackRates"
                    :playback-rate="playbackRate"
                    @prev="playPrev"
                    @next="playNext"
                    @toggle-flip="toggleFlip"
                    @toggle-full="toggleFull"
                    @change-rate="handleChangeRate"
                    @change-time="changeTime"
                    @toggle-pip="togglePip"
                    @change-volume="changeVolume"
                    @refresh="handleRefresh"
                  />
                </slot>
              </template>
            </div>
          </section>
        </div>
        <slot name="extra" />
      </div>
    </div>
  </FullScreen>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  shallowReadonly,
  watch,
} from 'vue'
import FullScreen from '@lit-element/components/fullScreen'
import { defaultNamespace, useNamespace } from '@lit-element/hooks'
import { isClient } from '@vueuse/core'
import { NOOP } from '@lit-element/utils'
import Progress from '@lit-element/components/progress'
import { LtIcon } from '@lit-element/components/icon'
import { useSetTimeout } from '@lit-element/components/captcha/src/hooks/useSetTimeout'
import { Loading, VideoPause, VideoPlay } from '@inte-cloud/icons-vue'
import { useListener } from './hooks/listener'
import { useModifier } from './hooks/modifier'
import VideoProgress from './video-progress.vue'
import { VIDEO_STATE, decide, videoDefaultShortcuts } from './symbol'
import { videoProps } from './video'
import Control from './control.vue'

defineOptions({ name: 'LtVideo' })
const props = defineProps(videoProps)
const emit = defineEmits([
  'play',
  'pause',
  'ended',
  'prev',
  'next',
  'refresh',
  'togglePip',
  'toggleFlip',
  'toggleFull',
  'update:src',
  'update:time',
  'update:volume',
  'update:playback-rate',
])

const ns = useNamespace('video')

const { timer } = useSetTimeout()

const pipEnabled = isClient && document.pictureInPictureEnabled

const currentSrc = ref(
  props.src || props.srcList?.[0] || (props.videoAttrs?.src as string) || ''
)
const playing = ref(false)
const currentTime = ref(props.time)
const canPlay = ref(false)
const duration = ref(0)
const currentVolume = ref(props.volume) // 0 ~ 1
const pip = ref(false)
const stateShow = ref(true)
const stateHidden = ref(false)
const interacting = ref(false)
const hasPlayed = ref(false)
const flipped = ref(false)
const iconScale = ref(1.3)

const screen = ref()
const wrapper = computed(() => screen.value?.containerElement)
const video = ref<HTMLVideoElement>()

const controlBottomList = computed(() => [
  {
    class: ns.e('controls-left'),
    list: props.controlLayout.left,
  },
  {
    class: ns.e('controls-center'),
    list: props.controlLayout.center,
  },
  {
    class: ns.e('controls-right'),
    list: props.controlLayout.right,
  },
])

const placeId = computed(() => screen.value?.placeId)
const full = computed<false | any>(() => {
  const s = screen.value
  if (s) {
    if (s.isOnlyShowContainer) {
      return 'window'
    }
    if (s.isFullScreen) {
      return 'browser'
    }
  }
  return false
})
const videoRef = computed(() => video.value || props.video)

const shortcutRecord = reactive({} as Record<string, () => void>)
const shortcuts = computed(() => ({
  ...videoDefaultShortcuts,
  ...props.shortcuts,
}))

const slotParams = shallowReadonly(
  reactive({
    playing,
    currentTime,
    currentVolume,
    // currentRate,
    interacting,
    flipped,
    canPlay,
    duration,
    pip,
    hasPlayed,
    togglePlaying,
    togglePip,
    // changeRate,
    changeTime,
    changeVolume,
    toggleFull,
  })
)

function addShortcut(key: string, cb: () => void) {
  if (shortcutRecord[key]) {
    console.warn(
      `[vexip-ui:Video] duplicate shortcut key '${key}' and it will be ignored`
    )

    return NOOP
  }

  shortcutRecord[key] = cb

  return () => {
    delete shortcutRecord[key]
  }
}

useModifier({
  target: wrapper,
  passive: false,
  onKeyDown: (event: any, modifier: any) => {
    if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return

    decide(
      [
        [
          () => modifier.up,
          () => {
            currentVolume.value = Math.min(1, currentVolume.value + 0.05)
          },
        ],
        [
          () => modifier.down,
          () => (currentVolume.value = Math.max(0, currentVolume.value - 0.05)),
        ],
        [
          () => modifier.right,
          () =>
            (currentTime.value = Math.min(
              duration.value,
              currentTime.value + 5
            )),
        ],
        [
          () => modifier.left,
          () => (currentTime.value = Math.max(0, currentTime.value - 5)),
        ],
        // [
        //   () => modifier[shortcuts.value.volume || 'M'],
        //   () => volumeRef.value?.toggleMute(),
        // ],
        ...Object.keys(shortcutRecord).map((key) => {
          return [() => modifier[key], shortcutRecord[key]] as [
            () => boolean,
            () => void
          ]
        }),
      ],
      { afterMatchAny: modifier.resetAll }
    )
  },
})

const className = computed(() => {
  return [
    ns.b(),
    ns.b('vars'),
    {
      [ns.m('loading')]: props.loading,
    },
  ]
})

const segments = computed(() => {
  return props.segments
    .map((segment: any) =>
      typeof segment === 'number' ? { time: segment } : segment
    )
    .filter((segment) => segment.time >= 0 && segment.time <= duration.value)
    .sort((prev, next) => prev.time - next.time)
})
const percent = computed(() => {
  return duration.value ? (currentTime.value / duration.value) * 100 : 0
})
const srcFullList = computed(() => {
  const src: any = props.src || (props.videoAttrs?.src as string) || ''

  if (src && props.srcList && !props.srcList.includes(src)) {
    return [src].concat(props.srcList)
  }

  return props.srcList || [src]
})
const srcIndex = computed(() => {
  return srcFullList.value ? srcFullList.value.indexOf(currentSrc.value) : -1
})

watch(
  () => props.src,
  (value: any) => {
    resetMetaState()
    currentSrc.value = value
  },
  { flush: 'pre' }
)
watch(
  () => props.time,
  (value) => changeTime(value, false)
)
watch(
  () => props.volume,
  (value) => {
    changeVolume(value, false)
  }
)

watch(playing, (value) => {
  if (value) {
    requestAnimationFrame(() => {
      stateShow.value = false
    })
  } else {
    stateShow.value = true
  }
})
watch(canPlay, (value) => {
  if (!value) {
    playing.value = false
  }
})
watch(stateShow, (value) => {
  if (value) {
    stateHidden.value = false
  }
})

onMounted(() => {
  nextTick(() => {
    if (videoRef.value && currentTime.value > 0) {
      videoRef.value.currentTime = currentTime.value
    }

    console.log(wrapper.value)
  })
})

provide(VIDEO_STATE, reactive({ placeId, iconScale, addShortcut }))

useListener(videoRef, 'canplay', () => {
  canPlay.value = true
  duration.value = videoRef.value?.duration ?? 0
})
useListener(videoRef, 'timeupdate', () => {
  currentTime.value = videoRef.value?.currentTime ?? 0
  emit('update:time', currentTime.value)
})
useListener(videoRef, 'ended', handleEnded)

useListener(videoRef, 'enterpictureinpicture', () => {
  pip.value = true
  emit('togglePip', true)
})
useListener(videoRef, 'leavepictureinpicture', () => {
  pip.value = false
  emit('togglePip', false)
})

defineExpose({
  currentSrc,
  playing,
  currentTime,
  duration,
  pip,
  interacting,
  stateHidden,
  wrapper,
  video,
  resetMetaState,
})

function toggleFlip(value = !flipped.value, dispatch = true) {
  flipped.value = value
  if (dispatch) {
    emit('toggleFlip', flipped.value)
  }
}

async function togglePip() {
  if (!pipEnabled || !videoRef.value) return

  if (pip.value) {
    await document.exitPictureInPicture()
  } else {
    await videoRef.value.requestPictureInPicture()
  }
}

function togglePlaying(value = !playing.value) {
  if (!canPlay.value) {
    playing.value = false

    return
  }

  playing.value = value

  if (playing.value) {
    hasPlayed.value = true
    videoRef.value?.play()
  } else {
    videoRef.value?.pause()
  }
  wrapper.value?.focus()
  emit(playing.value ? 'play' : 'pause')
}

function handleEnded() {
  playing.value = false

  videoRef.value?.pause()
  emit('ended')
}

function changeTime(time: number, dispatch = true) {
  currentTime.value = time

  if (videoRef.value && time !== videoRef.value.currentTime) {
    videoRef.value.currentTime = time
    time = videoRef.value.currentTime
  }
  if (dispatch) {
    emit('update:time', time)
  }
}

function changeVolume(volume: number, dispatch = true) {
  currentVolume.value = volume

  if (videoRef.value) {
    videoRef.value.volume = volume
  }

  if (dispatch) {
    emit('update:volume', volume)
  }
}

function resetMetaState() {
  playing.value = false
  currentTime.value = 0
  canPlay.value = false
  duration.value = 0
  // loadedData.value = false
  pip.value = false
  hasPlayed.value = false

  videoRef.value?.pause()
}
function handleInteract() {
  clearTimeout(timer.interact)

  interacting.value = true
  timer.interact = setTimeout(() => {
    interacting.value = false
  }, 3000)
}

function handlePointerLeave() {
  clearTimeout(timer.interact)
  timer.interact = setTimeout(() => {
    interacting.value = false
  }, 500)
}
function adjustSrc(amount: number) {
  const list = srcFullList.value

  if (props.srcList) {
    currentSrc.value =
      list[(srcIndex.value + amount + list.length) % list.length]
    emit('update:src', currentSrc.value)
  }
}

function playPrev() {
  adjustSrc(-1)
  emit('prev')
}

function playNext() {
  adjustSrc(1)
  emit('next')
}

function handleChangeRate(rate: number, dispatch = true) {
  if (videoRef.value) {
    videoRef.value.playbackRate = rate
  }
  if (dispatch) {
    emit('update:playback-rate', rate)
  }
}

function toggleFull(type: any) {
  screen.value?.toggleMode(type)
  wrapper.value?.focus()
  nextTick(() => {
    emit('toggleFull', full.value)
  })
}

function handleRefresh() {
  emit('refresh')
}
</script>
